// apps/console/src/components/organisms/AlertInbox/EnrollFaceModal.tsx
//
// "Enroll this face" modal (face-rec C4-4). Opened from an AlertInbox row whose
// alert carries a face snapshot. Pre-fills the name from the alert's identity /
// name and copies the EXISTING snapshot object server-side (it sends
// `sourceSnapshotRef = alert.snapshotRef`, NO imageBase64 — no re-upload) so the
// face lands in the gallery the Jetson recognizer syncs.
//
// Hand-rolled fixed-overlay (same shape as DashboardRobotTeleop): backdrop click
// + Escape close, inner stopPropagation. The snapshot preview reuses the shared
// <AlertSnapshot> resolver, with the alert's robotId: a helmet alarm's photo is
// then the link the alert log already cached for that device, and its resolve
// (if any) is narrowed to that device rather than any helmet in the tenant.

import { robotsApi } from '@/services/api/domains/robots.api'
import { X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { AlertSnapshot } from './AlertSnapshot'

type FaceRole = 'friend' | 'target'

export interface EnrollFaceModalProps {
  open: boolean
  /** The alert's snapshot media key — copied server-side as the gallery crop. */
  snapshotRef: string
  /** Pre-fills the name input (the alert's identity ?? name). */
  defaultName?: string
  /** Optional per-robot scope for the enrolled face. */
  robotId?: string
  onClose: () => void
  onEnrolled?: () => void
}

export function EnrollFaceModal({
  open,
  snapshotRef,
  defaultName,
  robotId,
  onClose,
  onEnrolled,
}: EnrollFaceModalProps) {
  const [name, setName] = useState(defaultName ?? '')
  const [role, setRole] = useState<FaceRole>('friend')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Reset the form whenever the modal (re)opens for an alert — a fresh open
  // should start from that alert's defaults, not the previous enrollment.
  useEffect(() => {
    if (!open) return

    setName(defaultName ?? '')
    setRole('friend')
    setError(null)
    setBusy(false)
  }, [open, snapshotRef, defaultName])

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleEnroll = useCallback(async () => {
    setError(null)

    const trimmed = name.trim()

    if (!trimmed) {
      setError('Enter a name for this face.')

      return
    }

    setBusy(true)
    try {
      await robotsApi.enrollFace({
        name: trimmed,
        role,
        robotId,
        sourceSnapshotRef: snapshotRef,
      })
      onEnrolled?.()
      onClose()
    } catch {
      setError('Enrollment failed. Please try again.')
      setBusy(false)
    }
  }, [name, role, robotId, snapshotRef, onClose, onEnrolled])

  if (!open) return null

  const fieldInput =
    'w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500'
  const fieldLabel = 'mb-1 block text-xs font-medium uppercase tracking-wide text-gray-700 dark:text-gray-300'

  return (
    <div
      data-testid="enroll-face-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Enroll this face"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-sm flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-900"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Enroll this face</h3>
          <button
            type="button"
            aria-label="Close"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form
          className="space-y-3 p-4"
          onSubmit={e => {
            e.preventDefault()
            void handleEnroll()
          }}
        >
          <div className="flex justify-center">
            <AlertSnapshot
              refKey={snapshotRef}
              robotId={robotId}
              alt="face to enroll"
              className="h-24 w-24 rounded object-cover ring-1 ring-gray-200 dark:ring-white/10"
            />
          </div>

          <div>
            <label htmlFor="enroll-face-name" className={fieldLabel}>
              Name
            </label>
            <input
              id="enroll-face-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Jane Doe"
              className={fieldInput}
            />
          </div>

          <div>
            <label htmlFor="enroll-face-role" className={fieldLabel}>
              Role
            </label>
            <select
              id="enroll-face-role"
              value={role}
              onChange={e => setRole(e.target.value === 'target' ? 'target' : 'friend')}
              className={fieldInput}
            >
              <option value="friend">Friend</option>
              <option value="target">Target</option>
            </select>
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
